import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bot, CheckCircle2 } from "lucide-react";

function fmt(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export default function SuperSupportAi() {
  const [data, setData] = useState({ paused: [], paused_count: 0, total_tenants: 0 });
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/platform/support-ai").then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); }, []);

  const enable = async (t) => {
    setBusy(t.id);
    try {
      await api.put(`/platform/support-ai/${t.id}`, { enabled: true });
      toast.success(`Support AI enabled for ${t.name}`);
      load();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Bot className="w-7 h-7 text-indigo-300" /> Support AI</h1>
        <p className="text-slate-500 text-sm mt-1">Merchants who spammed the dashboard assistant are paused here. Review and turn AI back on.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Card className="bg-slate-900 border-slate-800 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Paused</div>
          <div className="text-2xl font-bold font-mono-nums mt-1" data-testid="support-ai-paused-count">{data.paused_count}</div>
        </Card>
        <Card className="bg-slate-900 border-slate-800 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Stores</div>
          <div className="text-2xl font-bold font-mono-nums mt-1">{data.total_tenants}</div>
        </Card>
      </div>
      <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid="support-ai-paused-list">
        {(data.paused || []).length === 0 ? (
          <div className="p-8 text-sm text-slate-500 text-center">No merchants are paused. Spam auto-disable will appear here.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {(data.paused || []).map((t) => (
              <div key={t.id} className="p-4 flex items-start gap-4" data-testid={`support-ai-paused-${t.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-100 truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 font-mono-nums">/{t.slug}</div>
                  <div className="text-xs text-amber-200/90 mt-1">{t.support_ai_disabled_reason || "Paused"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{fmt(t.support_ai_disabled_at)}</div>
                </div>
                <Button
                  size="sm"
                  disabled={busy === t.id}
                  onClick={() => enable(t)}
                  data-testid={`support-ai-enable-${t.slug}`}
                  className="bg-indigo-500 hover:bg-indigo-600"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Enable AI
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
